import config from '@wandb/common/config';
import {PUBLISHED_PROJECT_NAME} from '@wandb/common/util/constants';
import {Link} from '@wandb/common/util/links';
import DOMPurify from 'dompurify';
import {History} from 'history';
import _ from 'lodash';
import React, {Fragment, ReactNode, useEffect, useMemo} from 'react';
import Helmet from 'react-helmet';
import {Redirect} from 'react-router';

import NewUserFooter from '../components/NewUserFooter';
import NoMatch from '../components/NoMatch';
import {ReportAdminBar} from '../components/ReportAdminBar';
import {ReportDiscussionContextProvider} from '../components/ReportDiscussionContext';
import ReportErrorBoundary from '../components/ReportErrorBoundary';
import ReportPageHeaderView from '../components/ReportPageHeaderView';
import WBSlateReduxBridge from '../components/Slate/WBSlateReduxBridge';
import {useMeasureComponentSpan} from '../components/utility/useMeasureComponentSpan';
import {isInIframe} from '../setup';
import {useReportPageQuery} from '../state/graphql/reportPageQuery';
import {useDispatch, usePropsSelector, useSelector} from '../state/hooks';
import {PanelBankContextProvider} from '../state/panelbank/context';
import * as PollingActions from '../state/polling/actions';
import {useReport} from '../state/reports/hooks';
import * as ReportSelectors from '../state/reports/selectors';
import * as ReportTypes from '../state/reports/types';
import {ReportViewRef} from '../state/reports/types';
import {RunQueryContext} from '../state/runs/context';
import * as ViewerHooks from '../state/viewer/hooks';
import {Viewer} from '../state/viewer/types';
import {useAdminModeActive} from '../util/admin';
import {
  maybeLengthenReportDescription,
  setDocumentDescription,
  setDocumentTitle,
} from '../util/document';
import {
  DEFAULT_LANGUAGE,
  DISCUSSION_CATEGORY_ID,
  getFCPathForTag,
  getHREFLang,
  getTranslationData,
  ReportIDWithTagIDs,
  ReportMetadata,
  rssLinkTagFCReports,
  rssLinkTagPublicReports,
  useGallerySpec,
  useIsGalleryAdmin,
  useIsGalleryReport,
  useReportMetadata,
  useReportTags,
} from '../util/gallery';
import {navigateTo} from '../util/history';
import {trackPageViewed} from '../util/navigation';
import * as Url from '../util/url';
import {decodeURIComponentSafe} from '../util/url';
import * as Urls from '../util/urls';
import {reportView} from '../util/urls';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';
import FC from './FC';
import * as S from './ReportPage.styles';

interface ReportPageViewPublishedWorkProps {
  match: {
    params: {
      entityName: string;
      reportNameAndID: string;
    };
  };
  history: History;
  location: Location;
}

export const ReportPageViewPublishedWork = (
  props: ReportPageViewPublishedWorkProps
) => {
  const {entityName, reportNameAndID} = props.match.params;
  let reportID = '';
  try {
    reportID = Url.parseNameAndID(reportNameAndID).id;
  } catch {
    return <NoMatch />;
  }
  return (
    <ReportPageViewInner
      entityName={entityName}
      projectName={PUBLISHED_PROJECT_NAME}
      reportID={reportID}
      history={props.history}
      projectReadOnly
      publishedWork
    />
  );
};

interface ReportPageViewValidatorProps {
  match: {
    params: {
      entityName: string;
      projectName?: string;
      reportNameAndID: string;
    };
  };
  history: History;
  location: Location;
}

const ReportPageViewValidator: React.FC<ReportPageViewValidatorProps> =
  props => {
    // for page view tracking
    useEffect(() => {
      trackPageViewed('Report', 'Viewing');
    }, []);

    const {entityName, reportNameAndID} = props.match.params;
    // HAX: the project name URI we get from react-router might be encoded.
    // Decode this URI component before using it.
    // If this happens again on another URI component (e.g. entity name), we
    // should do more general fix at the react-router layer.
    const projectName =
      props.match.params.projectName != null
        ? decodeURIComponentSafe(props.match.params.projectName)
        : null;
    let reportID = '';
    try {
      reportID = Url.parseNameAndID(reportNameAndID).id;
    } catch {
      return (
        <Redirect
          to={
            projectName != null
              ? Urls.reportList({entityName, projectName}) + '#404'
              : Urls.reportGallery()
          }
        />
      );
    }

    return (
      <ReportPageViewContent
        {...props}
        projectName={projectName}
        reportID={reportID}
      />
    );
  };

type ReportPageViewContentProps = ReportPageViewValidatorProps & {
  projectName: string | null;
  reportID: string;
};

const ReportPageViewContent: React.FC<ReportPageViewContentProps> = props => {
  const {match, projectName, reportID} = props;
  const {entityName} = match.params;

  const admin = useAdminModeActive();
  const galleryAdmin = useIsGalleryAdmin();
  const showFCView = !admin || galleryAdmin;

  const {loading: loadingIsGalleryReport, isGalleryReport} =
    useIsGalleryReport(reportID);
  if (loadingIsGalleryReport) {
    return null;
  }
  if (isGalleryReport && showFCView) {
    return <FC />;
  }

  return (
    <>
      <ReportPageViewMeta viewID={reportID} />
      <ReportErrorBoundary params={match.params}>
        {projectName != null ? (
          <ReportPageView
            entityName={entityName}
            projectName={projectName}
            reportID={reportID}
            history={props.history}
            isFeatured={isGalleryReport}
          />
        ) : (
          <GalleryPostView
            entityName={entityName}
            reportID={reportID}
            history={props.history}
          />
        )}
      </ReportErrorBoundary>
    </>
  );
};

interface ReportPageViewProps {
  entityName: string;
  projectName: string;
  reportID: string;
  history: History;
  isFeatured: boolean;
}

const ReportPageView: React.FC<ReportPageViewProps> = props => {
  const measureSpan = useMeasureComponentSpan('report-page-view-ui-appears', {
    singleUse: true,
  });
  // Note(adrnswanberg): I've factored most of ReportPageView into ReportPageViewInner so
  // that we can split up useReportPageQuery and useReport into separate components.
  // Main reason being: if someone doesn't have access to a ReportPage, we don't want
  // to try to load the report spec because then stuff still start breaking.
  const reportQuery = useReportPageQuery({
    entityName: props.entityName,
    projectName: props.projectName,
  });

  if (reportQuery.loading) {
    return <Loader name="report-query-loader" />;
  }

  if (reportQuery.project == null) {
    return <NoMatch />;
  }

  measureSpan({
    entityName: props.entityName,
    projectName: props.projectName,
    reportId: props.reportID,
  });

  return (
    <PanelBankContextProvider>
      <ReportPageViewInner
        entityName={props.entityName}
        projectName={props.projectName}
        reportID={props.reportID}
        history={props.history}
        projectReadOnly={reportQuery.project.readOnly}
        isFeatured={props.isFeatured}
      />
    </PanelBankContextProvider>
  );
};

type GalleryPostViewProps = {
  entityName: string;
  reportID: string;
  history: History;
};

const GalleryPostView: React.FC<GalleryPostViewProps> = props => {
  return (
    <PanelBankContextProvider>
      <GalleryPostViewInner
        entityName={props.entityName}
        reportID={props.reportID}
        history={props.history}
      />
    </PanelBankContextProvider>
  );
};

interface ReportPageViewInnerProps {
  entityName: string;
  projectName: string;
  history: History;
  reportID: string;
  projectReadOnly?: boolean;
  publishedWork?: boolean;
  isFeatured?: boolean;
}

const ReportPageViewInner: React.FC<ReportPageViewInnerProps> = props => {
  const dispatch = useDispatch();
  useEffect(() => {
    // Don't poll on the report page for now
    dispatch(PollingActions.setBasePollInterval(0));
  }, [dispatch]);

  const admin = useAdminModeActive();
  const viewer = ViewerHooks.useViewer();

  const reportCID = useReport(props.reportID);
  const reportViewID = usePropsSelector(
    ReportSelectors.makeReportViewIDSelector,
    reportCID
  );

  if (reportViewID.loading) {
    return <Loader name="report-view-id-loader" samplingRate={0.0002} />;
  }

  return (
    <ReportPageViewReady
      viewRef={reportViewID.viewID}
      viewer={viewer}
      history={props.history}
      entityName={props.entityName}
      projectName={props.projectName}
      editAccess={
        viewer != null && !isInIframe() && (!props.projectReadOnly || admin)
      }
      publishedWork={props.publishedWork}
      reportID={props.reportID}
      isFeatured={props.isFeatured}
    />
  );
};

interface GalleryPostViewInnerProps {
  entityName: string;
  history: History;
  reportID: string;
}

const GalleryPostViewInner: React.FC<GalleryPostViewInnerProps> = props => {
  const dispatch = useDispatch();
  useEffect(() => {
    // Don't poll on the report page for now
    dispatch(PollingActions.setBasePollInterval(0));
  }, [dispatch]);

  const admin = useAdminModeActive();
  const viewer = ViewerHooks.useViewer();

  const reportCID = useReport(props.reportID);
  const reportViewID = usePropsSelector(
    ReportSelectors.makeReportViewIDSelector,
    reportCID
  );

  const view = useSelector(state =>
    reportViewID.loading ? null : state.views.views[reportViewID.viewID.id]
  );
  const editAccess = (viewer != null && viewer.id === view?.user.id) || admin;

  if (reportViewID.loading) {
    return <Loader name="gallery-post-view-inner-loader" />;
  }

  return (
    <ReportPageViewReady
      viewRef={reportViewID.viewID}
      viewer={viewer}
      history={props.history}
      entityName={props.entityName}
      projectName=""
      editAccess={editAccess}
      reportID={props.reportID}
      isFeatured
    />
  );
};

interface ReportPageViewReadyProps {
  viewRef: ReportTypes.ReportViewRef;
  viewer?: Viewer;
  history: History;
  entityName: string;
  projectName: string;
  editAccess?: boolean;
  publishedWork?: boolean;
  reportID: string;
  isFeatured?: boolean;
}

const ReportPageViewReady: React.FC<ReportPageViewReadyProps> = props => {
  const {projectName, entityName, reportID, viewRef} = props;

  useEffect(() => {
    window.analytics?.track('Report Viewed', {
      reportID,
      projectName,
      entityName,
    });
  }, [entityName, projectName, reportID]);
  useSetReportTitleAndDescription(viewRef);

  const reportWidth = useSelector(ReportSelectors.getReportWidth(viewRef));

  const admin = useAdminModeActive();

  return (
    <div
      className={`report-page page-tabs read-only report-width-${reportWidth}`}>
      {!props.publishedWork && (
        <ReportPageViewUrl viewRef={viewRef} history={props.history} />
      )}
      <div className="report-page-top">
        {admin && <ReportAdminBar {...props} />}
        <S.ReportPageContainer isViewMode>
          <ReportDiscussionContextProvider
            reportViewRef={viewRef}
            reportServerID={props.reportID}>
            <ReportPageHeaderView
              viewRef={viewRef}
              editAccess={props.editAccess}
            />
            <RunQueryContext.Provider
              value={{
                entityName: props.entityName,
                projectName: props.projectName,
                report: {id: props.reportID},
              }}>
              <WBSlateReduxBridge
                readOnly={true}
                entityName={props.entityName}
                projectName={props.projectName}
                viewId={props.reportID}
                viewRef={viewRef}
              />
            </RunQueryContext.Provider>
          </ReportDiscussionContextProvider>
          {props.isFeatured && (
            <div className="report-gallery-text hide-on-print">
              Published with{' '}
              <span className="heart" role="img" aria-label="heart">
                ❤️
              </span>{' '}
              on Weights &amp; Biases. Read more reports in our community,{' '}
              <Link to={Urls.reportGallery()}>Fully Connected</Link>.
            </div>
          )}
          <div className="show-on-print report-created-footer">
            <p>
              Created with{' '}
              <span className="heart" role="img" aria-label="heart">
                ❤️
              </span>{' '}
              on Weights &amp; Biases.
            </p>
            {/* eslint-disable-next-line wandb/no-a-tags */}
            <a href={document.location.href}>{document.location.href}</a>
          </div>
        </S.ReportPageContainer>
      </div>
      <NewUserFooter whatFor="to create reports like this one." />
    </div>
  );
};

interface ReportPageViewUrlProps {
  viewRef: ReportViewRef;
  history: History;
}

const ReportPageViewUrl = (props: ReportPageViewUrlProps) => {
  // We do a deep comparison on this selector because if the view updates,
  // we end up getting a reference change on project. This page doesn't
  // actually update the view since you can't save here. But might as well
  // mirror the edit page behavior.
  const view = useSelector(state => {
    const {entityName, project, id, displayName} =
      state.views.views[props.viewRef.id];
    return {
      entityName,
      project,
      id,
      displayName,
    };
  }, _.isEqual);
  // Make sure url tracks report name
  useEffect(() => {
    if (view.id == null) {
      throw new Error('invalid state');
    }
    navigateTo({
      pathname:
        view.project != null
          ? Urls.reportView({
              entityName: view.project.entityName,
              projectName: view.project.name,
              reportID: view.id,
              reportName: view.displayName,
            })
          : (Url.isOnGalleryDiscussionView()
              ? Urls.galleryDiscussionView
              : Urls.galleryPostView)({
              entityName: view.entityName,
              reportID: view.id,
              reportName: view.displayName,
            }),
      preserveSearch: true,
      preserveHash: true,
      replace: true,
    });
  }, [props.history, view.displayName, view.id, view.entityName, view.project]);

  return null;
};

export default ReportPageViewValidator;

type ReportPageViewFCProps = {
  entityName: string;
  projectName?: string;
  reportID: string;
};

export const ReportPageViewFC: React.FC<ReportPageViewFCProps> = React.memo(
  ({entityName, projectName, reportID}) => {
    const params = useMemo(
      () => ({
        entityName,
        projectName,
        reportNameAndID: reportID,
      }),
      [entityName, projectName, reportID]
    );

    return (
      <>
        <ReportPageViewMeta viewID={reportID} />
        <ReportErrorBoundary params={params}>
          <FCReport
            entityName={entityName}
            projectName={projectName}
            reportID={reportID}
          />
        </ReportErrorBoundary>
      </>
    );
  }
);

type FCReportProps = {
  entityName: string;
  projectName?: string;
  reportID: string;
};

const FCReport: React.FC<FCReportProps> = React.memo(
  ({entityName, projectName, reportID}) => {
    return (
      <PanelBankContextProvider>
        <FCReportInner
          entityName={entityName}
          projectName={projectName}
          reportID={reportID}
        />
      </PanelBankContextProvider>
    );
  }
);

type FCReportInnerProps = {
  entityName: string;
  projectName?: string;
  reportID: string;
};

const FCReportInner: React.FC<FCReportInnerProps> = React.memo(
  ({entityName, projectName, reportID}) => {
    const dispatch = useDispatch();
    useEffect(() => {
      // Don't poll on the report page for now
      dispatch(PollingActions.setBasePollInterval(0));
    }, [dispatch]);

    const admin = useAdminModeActive();
    const viewer = ViewerHooks.useViewer();

    const reportCID = useReport(reportID);
    const reportViewID = usePropsSelector(
      ReportSelectors.makeReportViewIDSelector,
      reportCID
    );

    const view = useSelector(state =>
      reportViewID.loading ? null : state.views.views[reportViewID.viewID.id]
    );
    const editAccess = (viewer != null && viewer.id === view?.user.id) || admin;

    if (reportViewID.loading) {
      return <Loader name="fc-report-inner-loader" />;
    }

    return (
      <FCReportReady
        viewRef={reportViewID.viewID}
        entityName={entityName}
        projectName={projectName}
        editAccess={editAccess}
        reportID={reportID}
      />
    );
  }
);

type FCReportReadyProps = {
  viewRef: ReportTypes.ReportViewRef;
  entityName: string;
  projectName?: string;
  editAccess: boolean;
  reportID: string;
};

const FCReportReady: React.FC<FCReportReadyProps> = React.memo(props => {
  const {viewRef, entityName, projectName = '', editAccess, reportID} = props;

  useEffect(() => {
    window.analytics?.track('Report Viewed', {
      reportID,
      projectName,
      entityName,
    });
  }, [entityName, projectName, reportID]);
  useSetReportTitleAndDescription(viewRef);

  const reportWidth = useSelector(ReportSelectors.getReportWidth(viewRef));

  const admin = useAdminModeActive();

  return (
    <div
      className={`report-page page-tabs read-only report-width-${reportWidth}`}>
      <div className="report-page-top">
        {admin && <ReportAdminBar {...props} />}
        <S.ReportPageContainer isViewMode>
          <ReportPageHeaderView
            viewRef={viewRef}
            editAccess={editAccess}
            fullWidth
          />
          <ReportDiscussionContextProvider
            reportViewRef={viewRef}
            reportServerID={reportID}>
            <RunQueryContext.Provider
              value={{
                entityName,
                projectName,
                report: {id: reportID},
              }}>
              <WBSlateReduxBridge
                readOnly={true}
                entityName={entityName}
                projectName={projectName}
                viewId={reportID}
                viewRef={viewRef}
              />
            </RunQueryContext.Provider>
          </ReportDiscussionContextProvider>
          <FCReportTags reportID={reportID} />
          <FCReportCTA reportID={reportID} />
          <div className="report-gallery-text hide-on-print">
            Published with{' '}
            <span className="heart" role="img" aria-label="heart">
              ❤️
            </span>{' '}
            on Weights &amp; Biases. Read more reports in our community,{' '}
            <Link to={Urls.reportGallery()}>Fully Connected</Link>.
          </div>
          <div className="show-on-print report-created-footer">
            <p>
              Created with{' '}
              <span className="heart" role="img" aria-label="heart">
                ❤️
              </span>{' '}
              on Weights &amp; Biases.
            </p>
            {/* eslint-disable-next-line wandb/no-a-tags */}
            <a href={document.location.href}>{document.location.href}</a>
          </div>
        </S.ReportPageContainer>
      </div>
      <NewUserFooter whatFor="to create reports like this one." />
    </div>
  );
});

type FCReportTagsProps = {reportID: string};

const FCReportTags: React.FC<FCReportTagsProps> = React.memo(({reportID}) => {
  const {tags} = useReportTags(reportID);
  if (tags.length === 0) {
    return null;
  }
  return (
    <S.FCReportTags>
      Tags:{' '}
      {tags.map((t, i) => (
        <Fragment key={t.id}>
          {i > 0 && `, `}
          <S.FCReportTag to={getFCPathForTag(t)}>{t.name}</S.FCReportTag>
        </Fragment>
      ))}
    </S.FCReportTags>
  );
});

type FCReportCTAProps = {reportID: string};

const FCReportCTA: React.FC<FCReportCTAProps> = React.memo(({reportID}) => {
  const {primaryTag} = useReportTags(reportID, true);
  if (primaryTag == null || !primaryTag.cta) {
    return null;
  }
  return (
    <S.FCReportCTA
      className="report-gallery-text"
      dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(primaryTag.cta)}}
    />
  );
});

type ReportPageViewMetaProps = {
  viewID: string;
};

const ReportPageViewMeta: React.FC<ReportPageViewMetaProps> = React.memo(
  ({viewID}) => {
    return (
      <>
        <Helmet>
          {rssLinkTagFCReports}
          {rssLinkTagPublicReports}
        </Helmet>
        <ReportPageViewLanguageMeta viewID={viewID} />
      </>
    );
  }
);

type ReportPageViewLanguageMetaProps = ReportPageViewMetaProps;

const ReportPageViewLanguageMeta: React.FC<ReportPageViewLanguageMetaProps> =
  React.memo(({viewID}) => {
    const {
      gallerySpec: {reportIDsWithTagIDs},
    } = useGallerySpec();

    const translationData = useMemo(
      () => getTranslationData(reportIDsWithTagIDs, viewID),
      [reportIDsWithTagIDs, viewID]
    );

    if (translationData == null) {
      return null;
    }

    const {inputReport, originalReport, translations} = translationData;

    return (
      <ReportPageViewLanguageMetaInner
        currentReport={inputReport}
        originalReport={originalReport}
        translations={translations}
      />
    );
  });

type ReportPageViewLanguageMetaInnerProps = {
  currentReport: ReportIDWithTagIDs;
  originalReport: ReportIDWithTagIDs;
  translations: ReportIDWithTagIDs[];
};

const ReportPageViewLanguageMetaInner: React.FC<ReportPageViewLanguageMetaInnerProps> =
  React.memo(({currentReport, originalReport, translations}) => {
    useEffect(() => {
      const htmlEl = document.querySelector('html');
      if (htmlEl == null) {
        return;
      }

      htmlEl.lang = getHREFLang(currentReport.language);
      return () => {
        htmlEl.lang = getHREFLang(DEFAULT_LANGUAGE);
      };
    }, [currentReport]);

    const reportIDs = useMemo(() => {
      return [originalReport, ...translations].map(({id}) => id);
    }, [originalReport, translations]);
    const {reportMetadatas} = useReportMetadata(reportIDs);

    if (reportMetadatas == null) {
      return null;
    }

    const reportMetadataByID: Map<string, ReportMetadata> = new Map(
      reportMetadatas.map(r => [r.id, r])
    );

    const metaTags: ReactNode[] = [];
    let defaultMetaTag: ReactNode | null = null;
    for (const {id, language} of [originalReport, ...translations]) {
      const metadata = reportMetadataByID.get(id);
      if (metadata == null) {
        continue;
      }

      const {project, entityName, displayName, tags} = metadata;
      const href =
        config.HOST +
        reportView({
          entityName: project?.entityName ?? entityName,
          projectName: project?.name ?? '',
          reportID: id,
          reportName: displayName,
          isDiscussion:
            tags?.some(t => t.id === DISCUSSION_CATEGORY_ID) ?? false,
        });
      const hrefLang = getHREFLang(language);

      metaTags.push(
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
      );

      if (id === originalReport.id) {
        defaultMetaTag = (
          <link
            key="x-default"
            rel="alternate"
            hrefLang="x-default"
            href={href}
          />
        );
      }
    }
    if (defaultMetaTag != null) {
      metaTags.push(defaultMetaTag);
    }

    return <Helmet>{metaTags}</Helmet>;
  });

function useSetReportTitleAndDescription(viewRef: ReportViewRef): void {
  const view = useSelector(state => {
    const {
      displayName,
      description,
      user: {username, name},
    } = state.views.views[viewRef.id];
    return {
      displayName,
      description,
      user: {username, name},
    };
  }, _.isEqual);

  useEffect(() => {
    setDocumentTitle(view.displayName);
    setDocumentDescription(
      maybeLengthenReportDescription(view.description, view.user)
    );
  }, [view]);
}
